// [claude-code 2026-04-03] SiriOrb-based voice indicator.
// [Codex 2026-07-22] Shared theme-aware activity orb for voice, composition, tasks, and retrieval.
import { useMemo } from "react";
import type { VoiceOrbState } from "../../types/voice";

interface VoiceAuroraOrbProps {
  state?: VoiceOrbState;
  activity?: OrbActivity;
  compact?: boolean;
  size?: number;
}

export type OrbActivity = "composing" | "shaping" | "searching" | "working";

interface OrbConfig {
  c1: string;
  c2: string;
  c3: string;
  speed: number;
  border: string;
  direction?: "normal" | "reverse";
}

const THEME_COLORS = {
  c1: "color-mix(in srgb, var(--accent-primary) 82%, var(--fintheon-text) 18%)",
  c2: "color-mix(in srgb, var(--accent-primary) 58%, var(--fintheon-text) 42%)",
  c3: "color-mix(in srgb, var(--accent-primary) 36%, var(--fintheon-text) 64%)",
  border:
    "color-mix(in srgb, var(--accent-primary) 74%, var(--fintheon-text) 26%)",
};

const STATE_CONFIG: Record<VoiceOrbState, OrbConfig> = {
  idle: {
    ...THEME_COLORS,
    speed: 24,
  },
  listening: {
    ...THEME_COLORS,
    speed: 14,
  },
  speaking: {
    ...THEME_COLORS,
    speed: 8,
  },
  thinking: {
    ...THEME_COLORS,
    speed: 6,
  },
  complete: {
    ...THEME_COLORS,
    speed: 18,
  },
  error: {
    c1: "oklch(60% 0.20 25)",
    c2: "oklch(55% 0.18 15)",
    c3: "oklch(65% 0.22 30)",
    speed: 30,
    border: "#ef4444",
  },
  infraction: {
    c1: "oklch(60% 0.22 25)",
    c2: "oklch(55% 0.20 15)",
    c3: "oklch(65% 0.24 30)",
    speed: 4,
    border: "#ef4444",
  },
};

const ACTIVITY_CONFIG: Record<OrbActivity, OrbConfig> = {
  composing: { ...THEME_COLORS, speed: 5 },
  shaping: { ...THEME_COLORS, speed: 11 },
  searching: { ...THEME_COLORS, speed: 8, direction: "reverse" },
  working: { ...THEME_COLORS, speed: 15 },
};

export function VoiceAuroraOrb({
  state,
  activity,
  compact = false,
  size: sizeOverride,
}: VoiceAuroraOrbProps) {
  const size = sizeOverride ?? (compact ? 24 : 28);
  const cfg = activity
    ? ACTIVITY_CONFIG[activity]
    : STATE_CONFIG[state ?? "idle"];
  const blur = Math.max(size * 0.08, 4);
  const contrast = Math.max(size * 0.003, 1.6);
  const orbId = useMemo(
    () => `orb-${Math.random().toString(36).slice(2, 8)}`,
    [],
  );

  return (
    <>
      <style>{`
        @property --${orbId}-angle {
          syntax: "<angle>";
          inherits: false;
          initial-value: 0deg;
        }
        @keyframes ${orbId}-rotate {
          from { --${orbId}-angle: 0deg; }
          to { --${orbId}-angle: 360deg; }
        }
        @media (prefers-reduced-motion: reduce) {
          .voice-aurora-orb__field { animation: none !important; }
        }
      `}</style>

      <div
        aria-hidden="true"
        className="voice-aurora-orb relative rounded-full shrink-0"
        data-voice-aurora-orb="true"
        data-orb-activity={activity}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          border: `1.5px solid ${cfg.border}`,
          transition: "border-color 0.4s ease",
        }}
      >
        {/* Orb core */}
        <div
          style={{
            position: "absolute",
            inset: "1px",
            borderRadius: "50%",
            overflow: "hidden",
            display: "grid",
            gridTemplateAreas: '"stack"',
            background:
              "radial-gradient(circle, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.01) 30%, transparent 70%)",
          }}
        >
          <div
            className="voice-aurora-orb__field"
            style={{
              gridArea: "stack",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              background: [
                `conic-gradient(from calc(var(--${orbId}-angle) * 1.2) at 30% 65%, ${cfg.c3} 0deg, transparent 45deg 315deg, ${cfg.c3} 360deg)`,
                `conic-gradient(from calc(var(--${orbId}-angle) * 0.8) at 70% 35%, ${cfg.c2} 0deg, transparent 60deg 300deg, ${cfg.c2} 360deg)`,
                `conic-gradient(from calc(var(--${orbId}-angle) * -1.5) at 65% 75%, ${cfg.c1} 0deg, transparent 90deg 270deg, ${cfg.c1} 360deg)`,
                `conic-gradient(from calc(var(--${orbId}-angle) * 2.1) at 25% 25%, ${cfg.c2} 0deg, transparent 30deg 330deg, ${cfg.c2} 360deg)`,
                `radial-gradient(ellipse 120% 80% at 40% 60%, ${cfg.c3} 0%, transparent 50%)`,
              ].join(", "),
              filter: `blur(${blur}px) contrast(${contrast}) saturate(1.3)`,
              animation: `${orbId}-rotate ${cfg.speed}s linear infinite ${cfg.direction ?? "normal"}`,
              transform: "translateZ(0)",
              willChange: "transform",
              transition: "filter 0.4s ease",
            }}
          />
          {/* Gloss overlay */}
          <div
            style={{
              gridArea: "stack",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 45% 55%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.04) 30%, transparent 60%)",
              mixBlendMode: "overlay",
            }}
          />
        </div>
      </div>
    </>
  );
}
